<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-container glass">
      <!-- 头部 -->
      <div class="modal-header">
        <div class="header-content">
          <span class="handshake-icon glass-sm">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22c1.105 0 2-.895 2-2h-4a2 2 0 0 0 2 2z"/>
              <path d="M18 16v-5c0-3.071-1.638-5.647-4-6.88v-3.12a2 2 0 1 0-4 0v3.12c-2.362 1.233-4 3.809-4 6.88v5l-2 3v1h16v-1l-2-3z"/>
            </svg>
          </span>
          <h2 class="modal-title">{{ $t('clientModal.title') }}</h2>
        </div>
        <button class="close-btn" @click="closeModal" :aria-label="$t('common.close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- 表单内容 -->
      <div class="modal-body">
        <form novalidate @submit.prevent="handleSubmit">
          <!-- 第一行：姓名、公司 -->
          <div class="form-row two-cols">
            <div class="form-group">
              <label>{{ $t('clientModal.name') }} <span class="required">*</span></label>
              <input
                type="text"
                v-model="form.name"
                :placeholder="$t('clientModal.namePlaceholder')"
                :maxlength="fieldLimits.name"
                required
                class="glass-sm"
                @input="clearFieldError('name')"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.name" class="form-error">{{ fieldErrors.name }}</p>
                <span class="char-count">{{ form.name.length }}/{{ fieldLimits.name }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('clientModal.company') }} <span class="required">*</span></label>
              <input
                type="text"
                v-model="form.company"
                :placeholder="$t('clientModal.companyPlaceholder')"
                :maxlength="fieldLimits.company"
                required
                class="glass-sm"
                @input="clearFieldError('company')"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.company" class="form-error">{{ fieldErrors.company }}</p>
                <span class="char-count">{{ form.company.length }}/{{ fieldLimits.company }}</span>
              </div>
            </div>
          </div>

          <!-- 第二行：职位、手机、邮箱 -->
          <div class="form-row three-cols">
            <div class="form-group">
              <label>{{ $t('clientModal.position') }} <span class="required">*</span></label>
              <input
                type="text"
                v-model="form.position"
                :placeholder="$t('clientModal.positionPlaceholder')"
                :maxlength="fieldLimits.position"
                required
                class="glass-sm"
                @input="clearFieldError('position')"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.position" class="form-error">{{ fieldErrors.position }}</p>
                <span class="char-count">{{ form.position.length }}/{{ fieldLimits.position }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('clientModal.phone') }} <span class="required">*</span></label>
              <input
                type="tel"
                v-model="form.phone"
                :placeholder="$t('clientModal.phonePlaceholder')"
                :minlength="fieldLimits.phoneMin"
                :maxlength="fieldLimits.phoneMax"
                required
                class="glass-sm"
                @input="handlePhoneInput"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.phone" class="form-error">{{ fieldErrors.phone }}</p>
                <span class="char-count">{{ form.phone.length }}/{{ fieldLimits.phoneMax }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('clientModal.email') }} <span class="required">*</span></label>
              <input
                type="email"
                v-model="form.email"
                :placeholder="$t('clientModal.emailPlaceholder')"
                :maxlength="fieldLimits.email"
                required
                class="glass-sm"
                @input="handleEmailInput"
              />
              <div class="field-meta">
                <p v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</p>
                <span class="char-count">{{ form.email.length }}/{{ fieldLimits.email }}</span>
              </div>
            </div>
          </div>

          <!-- 第三行：备注 -->
          <div class="form-group">
            <label>{{ $t('clientModal.remark') }}</label>
            <textarea
              v-model="form.remark"
              rows="5"
              :placeholder="$t('clientModal.remarkPlaceholder')"
              :maxlength="fieldLimits.remark"
              class="glass-sm"
              @input="clearFieldError('remark')"
            ></textarea>
            <div class="field-meta">
              <p v-if="fieldErrors.remark" class="form-error">{{ fieldErrors.remark }}</p>
              <span class="char-count">{{ form.remark.length }}/{{ fieldLimits.remark }}</span>
            </div>
          </div>

          <!-- 提交按钮 -->
          <div class="submit-section">
            <button type="submit" class="submit-btn" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="btn-spinner" aria-hidden="true"></span>
              <span>{{ isSubmitting ? $t('clientModal.submitting') : $t('clientModal.submit') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { submitCustomerContact } from '@/api/websiteContact'
import { isEmailCharactersValid, isPhoneCharactersValid, sanitizeEmail, sanitizePhone } from '@/utils/contactForm'
import { showToast } from '@/composables/useToast'

const { t } = useI18n()
const emit = defineEmits(['close'])

const isSubmitting = ref(false)
const fieldLimits = {
  name: 20,
  company: 32,
  position: 32,
  phoneMin: 6,
  phoneMax: 20,
  email: 32,
  remark: 2000
}
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const form = reactive({
  name: '',
  company: '',
  position: '',
  phone: '',
  email: '',
  remark: ''
})

const fieldErrors = reactive({
  name: '',
  company: '',
  position: '',
  phone: '',
  email: '',
  remark: ''
})

const closeModal = () => {
  emit('close')
}

const clearFieldError = (field) => {
  fieldErrors[field] = ''
}

const handlePhoneInput = () => {
  form.phone = sanitizePhone(form.phone)
  clearFieldError('phone')
}

const handleEmailInput = () => {
  form.email = sanitizeEmail(form.email)
  clearFieldError('email')
}

const trimForm = () => {
  form.name = form.name.trim()
  form.company = form.company.trim()
  form.position = form.position.trim()
  form.phone = form.phone.trim()
  form.email = form.email.trim()
  form.remark = form.remark.trim()
}

const validateRequiredAndMaxLength = (field, maxLength, requiredKey, maxKey) => {
  const value = form[field].trim()
  if (!value) {
    fieldErrors[field] = t(requiredKey)
    return false
  }
  if (value.length > maxLength) {
    fieldErrors[field] = t(maxKey, { max: maxLength })
    return false
  }
  return true
}

const validateForm = () => {
  trimForm()
  Object.keys(fieldErrors).forEach((field) => {
    fieldErrors[field] = ''
  })

  let valid = true
  valid = validateRequiredAndMaxLength('name', fieldLimits.name, 'clientModal.nameRequired', 'clientModal.nameMaxLength') && valid
  valid = validateRequiredAndMaxLength('company', fieldLimits.company, 'clientModal.companyRequired', 'clientModal.companyMaxLength') && valid
  valid = validateRequiredAndMaxLength('position', fieldLimits.position, 'clientModal.positionRequired', 'clientModal.positionMaxLength') && valid

  if (!form.phone) {
    fieldErrors.phone = t('clientModal.phoneRequired')
    valid = false
  } else if (!isPhoneCharactersValid(form.phone)) {
    fieldErrors.phone = t('clientModal.phoneInvalidChars')
    valid = false
  } else if (form.phone.length < fieldLimits.phoneMin || form.phone.length > fieldLimits.phoneMax) {
    fieldErrors.phone = t('clientModal.phoneLength', {
      min: fieldLimits.phoneMin,
      max: fieldLimits.phoneMax
    })
    valid = false
  }

  if (!form.email) {
    fieldErrors.email = t('clientModal.emailRequired')
    valid = false
  } else if (!isEmailCharactersValid(form.email)) {
    fieldErrors.email = t('clientModal.emailInvalidChars')
    valid = false
  } else if (form.email.length > fieldLimits.email) {
    fieldErrors.email = t('clientModal.emailMaxLength', { max: fieldLimits.email })
    valid = false
  } else if (!emailPattern.test(form.email)) {
    fieldErrors.email = t('clientModal.emailFormat')
    valid = false
  }

  if (form.remark.length > fieldLimits.remark) {
    fieldErrors.remark = t('clientModal.remarkMaxLength', { max: fieldLimits.remark })
    valid = false
  }

  return valid
}

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return
  }
  if (!validateForm()) {
    return
  }

  try {
    isSubmitting.value = true
    await submitCustomerContact({
      name: form.name,
      company: form.company,
      position: form.position,
      mobile: form.phone,
      email: form.email,
      remark: form.remark
    })
    showToast(t('clientModal.successMessage'))
    closeModal()
  } catch (error) {
    showToast(error.message || t('clientModal.errorMessage'), 'error')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 39, 64, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 28px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow:
    0 25px 80px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.2) inset;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  background: linear-gradient(135deg, rgba(15, 39, 64, 0.95) 0%, rgba(26, 58, 92, 0.92) 100%);
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 28px 28px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 50%, rgba(52, 152, 255, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 40%);
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.handshake-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.2);
}

.handshake-icon svg {
  width: 24px;
  height: 24px;
}

.modal-title {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.close-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  cursor: pointer;
  padding: 10px;
  border-radius: 10px;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-50%) scale(1.05);
}

.close-btn:active {
  transform: translateY(-50%) scale(0.98);
}

.close-btn svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 36px;
}

.form-row {
  display: grid;
  gap: 20px;
  margin-bottom: 20px;
}

.form-row.three-cols {
  grid-template-columns: repeat(3, 1fr);
}

.form-row.two-cols {
  grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 768px) {
  .form-row.three-cols,
  .form-row.two-cols {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  color: var(--primary-color);
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.required {
  color: #e74c3c;
}

.form-group input,
.form-group textarea {
  padding: 14px 18px;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.5);
  color: var(--text-dark);
  font-size: 0.98rem;
  transition: all 0.3s ease;
  font-family: inherit;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(102, 102, 102, 0.6);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 4px rgba(52, 152, 255, 0.15), 0 4px 12px rgba(52, 152, 255, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-error {
  margin-top: 8px;
  color: #e74c3c !important;
  font-size: 0.84rem;
  line-height: 1.5;
}

.field-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.field-meta .form-error {
  margin-top: 0;
}

.char-count {
  margin-left: auto;
  color: var(--text-light);
  font-size: 0.82rem;
  line-height: 1.5;
  white-space: nowrap;
}

.submit-section {
  text-align: center;
  margin-top: 32px;
}

.submit-btn {
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2980b9 100%);
  color: #fff;
  border: none;
  padding: 16px 64px;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2980b9 0%, var(--secondary-color) 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(52, 152, 255, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: submitSpin 0.8s linear infinite;
}

@keyframes submitSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 16px;
  }

  .modal-container {
    max-height: 95vh;
    border-radius: 24px;
  }

  .modal-header {
    padding: 18px 24px;
    border-radius: 24px 24px 0 0;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .handshake-icon {
    width: 40px;
    height: 40px;
  }

  .handshake-icon svg {
    width: 20px;
    height: 20px;
  }

  .close-btn {
    right: 16px;
    padding: 8px;
  }

  .modal-body {
    padding: 24px;
  }

  .form-group label {
    font-size: 0.88rem;
  }

  .form-group input,
  .form-group textarea {
    padding: 12px 16px;
    font-size: 0.95rem;
  }

  .submit-btn {
    padding: 14px 48px;
    font-size: 1rem;
    width: 100%;
  }
}
</style>
